import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import { Plus, Search, Filter, Edit, Trash2, Eye, Calendar, FileText, Image as ImageIcon, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { blogService, Post as BlogPost, BlogStats, Category as BlogCategory } from "../services/blog";
import { toast } from "sonner";
import { BlogCategorias } from "../components/BlogCategorias";
import { stripHtml, getImageUrl } from "../utils";

export function BlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [stats, setStats] = useState<BlogStats>({
    total_posts: 0,
    published_posts: 0,
    total_views: 0,
    drafts: 0
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(undefined);
  const [selectedYear, setSelectedYear] = useState<string | undefined>(undefined);

  const loadData = async () => {
    try {
      setLoading(true);

      // Carrega categorias para o filtro
      const categoriesData = await blogService.getCategories();
      setCategories(categoriesData || []);

      const [postsData, statsData] = await Promise.all([
        blogService.getAdminPosts({
          search,
          page: currentPage,
          per_page: 10,
          category_id: selectedCategory ? parseInt(selectedCategory, 10) : undefined,
          month: selectedMonth,
          year: selectedYear
        }),
        blogService.getBlogStats()
      ]);

      // API retorna paginação: { data: [...], current_page, last_page, total }
      if (postsData.data) {
        setPosts(postsData.data);
        setCurrentPage(postsData.current_page || 1);
        setTotalPages(postsData.last_page || 1);
        setTotalItems(postsData.total || 0);
      } else {
        setPosts(postsData);
      }
      setStats(statsData);
    } catch (error) {
      console.error("Erro ao carregar dados do blog:", error);
      toast.error("Erro ao carregar posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, search, selectedCategory, selectedMonth, selectedYear]);

  const handleEdit = (post: BlogPost) => {
    navigate(`/blog/${post.id}`);
  };

  const handleViewPost = (post: BlogPost) => {
    const storeUrl = import.meta.env.VITE_STORE_URL || "";
    const slug = post.slug || post.id;
    window.open(`${storeUrl}/blog/${slug}`, "_blank");
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir este post?")) return;

    try {
      await blogService.deletePost(id);
      toast.success("Post removido com sucesso");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover post");
    }
  };

  const getStatusBadge = (status: string, _publishedAt?: string) => {
    const statusUpper = status.toUpperCase();

    switch (statusUpper) {
      case 'PUBLISHED':
        return <Badge className="bg-[#849D70] text-white border-[#849D70]">Publicado</Badge>;
      case 'DRAFT':
        return <Badge className="bg-[#D4C070] text-white border-[#D4C070]">Rascunho</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const clearFilters = () => {
    setSelectedCategory(undefined);
    setSelectedMonth(undefined);
    setSelectedYear(undefined);
    setSearch("");
    setCurrentPage(1);
  };

  const hasActiveFilters = selectedCategory || selectedMonth || selectedYear || search;

  return (
    <main className="flex-1 p-6 bg-gray-50">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#68686A]">Blog</h1>
        <p className="text-[#68686A]">Gerencie o conteúdo do blog, artigos e categorias</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Total de Posts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{stats.total_posts}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Publicados</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#849D70]">{stats.published_posts}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Rascunhos</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-[#D4C070]">{stats.drafts}</p>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#68686A]">Total de Views</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">{stats.total_views.toLocaleString('pt-BR')}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="posts" className="space-y-6">
        <div className="flex items-center gap-2">
          <TabsList className="bg-[#EEF1F2] p-1 rounded-lg inline-flex gap-1">
            <TabsTrigger
              value="posts"
              className="data-[state=active]:bg-white data-[state=active]:text-[#D4C070] data-[state=active]:shadow-sm text-[#68686A] hover:text-[#D4C070] transition-all px-4 py-2 text-sm font-medium rounded-md"
            >
              <FileText className="h-4 w-4 mr-2" />
              Posts
            </TabsTrigger>
            <TabsTrigger
              value="categorias"
              className="data-[state=active]:bg-white data-[state=active]:text-[#D4C070] data-[state=active]:shadow-sm text-[#68686A] hover:text-[#D4C070] transition-all px-4 py-2 text-sm font-medium rounded-md"
            >
              <Filter className="h-4 w-4 mr-2" />
              Categorias
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="posts" className="space-y-6 m-0 pt-2">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar posts..."
                className="pl-10 border-gray-300 focus:ring-2 focus:ring-[#D4C070] focus:border-transparent"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              onClick={() => window.location.href = "/blog/novo"}
              className="bg-[#D4C070] hover:bg-[#849D70] text-white whitespace-nowrap"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Post
            </Button>
          </div>

          {/* Filtros */}
          <div className="flex flex-wrap items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-[#68686A]" />
              <span className="text-sm font-medium text-[#68686A]">Filtros:</span>
            </div>

            {/* Filtro de Categoria */}
            <select
              value={selectedCategory || ""}
              onChange={(e) => {
                setSelectedCategory(e.target.value || undefined);
                setCurrentPage(1);
              }}
              className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
            >
              <option value="">Todas categorias</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Filtro de Mês */}
            <select
              value={selectedMonth || ""}
              onChange={(e) => {
                setSelectedMonth(e.target.value || undefined);
                setCurrentPage(1);
              }}
              className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
            >
              <option value="">Todos meses</option>
              <option value="1">Janeiro</option>
              <option value="2">Fevereiro</option>
              <option value="3">Março</option>
              <option value="4">Abril</option>
              <option value="5">Maio</option>
              <option value="6">Junho</option>
              <option value="7">Julho</option>
              <option value="8">Agosto</option>
              <option value="9">Setembro</option>
              <option value="10">Outubro</option>
              <option value="11">Novembro</option>
              <option value="12">Dezembro</option>
            </select>

            {/* Filtro de Ano */}
            <select
              value={selectedYear || ""}
              onChange={(e) => {
                setSelectedYear(e.target.value || undefined);
                setCurrentPage(1);
              }}
              className="h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
            >
              <option value="">Todos anos</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
              <option value="2021">2021</option>
              <option value="2020">2020</option>
            </select>
            {/*TODO: Adicionar filtro dinâmico de anos */}
            {/* Botão Limpar Filtros */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="h-9 border-gray-300 text-red-600 hover:bg-red-50 hover:border-red-300 text-sm"
              >
                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpar
              </Button>
            )}
          </div>

          <Card className="mb-6 border border-gray-200">
            <CardHeader>
              <CardTitle className="text-[#68686A]">Lista de Posts</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#D4C070]" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Versão Desktop - Tabela */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 text-[#68686A]">Imagem</th>
                          <th className="text-left py-3 px-4 text-[#68686A]">Informações do Post</th>
                          <th className="text-left py-3 px-4 text-[#68686A]">Status</th>
                          <th className="text-left py-3 px-4 text-[#68686A]">Última Atividade</th>
                          <th className="text-left py-3 px-4 text-[#68686A]">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-gray-500">
                              Nenhum post encontrado.
                            </td>
                          </tr>
                        ) : (
                          posts.map((post) => (
                            <tr key={post.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                {post.image ? (
                                  <img
                                    src={getImageUrl(post.image)}
                                    alt={post.title}
                                    className="w-16 h-12 object-cover rounded"
                                  />
                                ) : (
                                  <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">
                                    <ImageIcon className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <div className="space-y-1">
                                  <div className="flex items-start gap-2">
                                    <FileText className="h-4 w-4 text-[#68686A] mt-0.5 flex-shrink-0" />
                                    <span className="font-medium text-gray-900 line-clamp-1">{post.title}</span>
                                  </div>
                                  {post.summary && (
                                    <p className="text-xs text-gray-500 line-clamp-2">
                                      {stripHtml(post.summary)}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2">
                                    {post.categories && post.categories.length > 0 && (
                                      <Badge variant="outline" className="text-[10px] h-4 border-gray-300 text-[#68686A]">
                                        {post.categories[0].name}
                                        {post.categories.length > 1 && ` +${post.categories.length - 1}`}
                                      </Badge>
                                    )}
                                    <div className="flex items-center gap-1 text-xs text-[#68686A]">
                                      <Eye className="h-3 w-3" />
                                      <span>{post.viewed?.toLocaleString('pt-BR')}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                {getStatusBadge(post.status, post.published_at)}
                              </td>
                              <td className="py-3 px-4 text-sm text-[#68686A]">
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(post.published_at || post.created_at || '').toLocaleDateString('pt-BR')}
                                  </div>
                                  {post.published_at && post.status === 'PUBLISHED' && new Date(post.published_at) > new Date() && (
                                    <span className="text-[10px] text-blue-600">
                                      Agendado: {new Date(post.published_at).toLocaleDateString('pt-BR')}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-gray-400">
                                    {post.author?.name || 'Sistema'}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                                    onClick={() => handleEdit(post)}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                                    onClick={() => handleViewPost(post)}
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                                    onClick={() => post.id && handleDelete(post.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Versão Mobile - Cards Empilhados */}
                  <div className="md:hidden space-y-4">
                    {posts.length === 0 ? (
                      <p className="py-8 text-center text-gray-500">
                        Nenhum post encontrado.
                      </p>
                    ) : (
                      posts.map((post) => (
                        <Card key={post.id} className="border border-gray-200">
                          <CardContent className="p-4 space-y-3">
                            {/* Imagem e Status */}
                            <div className="flex items-start gap-3">
                              {post.image ? (
                                <img
                                  src={getImageUrl(post.image)}
                                  alt={post.title}
                                  className="w-20 h-16 object-cover rounded flex-shrink-0"
                                />
                              ) : (
                                <div className="w-20 h-16 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                  <ImageIcon className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1">
                                {getStatusBadge(post.status, post.published_at)}
                              </div>
                            </div>

                            {/* Título e Resumo */}
                            <div className="space-y-2">
                              <div className="flex items-start gap-2">
                                <FileText className="h-4 w-4 text-[#68686A] mt-0.5 flex-shrink-0" />
                                <span className="font-medium text-gray-900">{post.title}</span>
                              </div>
                              {post.summary && (
                                <p className="text-sm text-gray-500 line-clamp-2">
                                  {stripHtml(post.summary)}
                                </p>
                              )}
                            </div>

                            {/* Categoria e Views */}
                            <div className="flex items-center gap-3 flex-wrap">
                              {post.categories && post.categories.length > 0 && (
                                <Badge variant="outline" className="text-[10px] h-4 border-gray-300 text-[#68686A]">
                                  {post.categories[0].name}
                                  {post.categories.length > 1 && ` +${post.categories.length - 1}`}
                                </Badge>
                              )}
                              <div className="flex items-center gap-1 text-xs text-[#68686A]">
                                <Eye className="h-3 w-3" />
                                <span>{post.viewed?.toLocaleString('pt-BR')} views</span>
                              </div>
                            </div>

                            {/* Data e Autor */}
                            <div className="flex flex-col text-sm text-[#68686A]">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(post.published_at || post.created_at || '').toLocaleDateString('pt-BR')}
                              </div>
                              {post.published_at && post.status === 'PUBLISHED' && new Date(post.published_at) > new Date() && (
                                <span className="text-[10px] text-blue-600">
                                  Agendado: {new Date(post.published_at).toLocaleDateString('pt-BR')}
                                </span>
                              )}
                              <span className="text-[10px] text-gray-400">
                                por {post.author?.name || 'Sistema'}
                              </span>
                            </div>

                            {/* Botões de Ação */}
                            <div className="flex gap-2 pt-2 border-t">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                                onClick={() => handleEdit(post)}
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 text-[#68686A] hover:bg-[#EEF1F2] hover:text-[#D4C070] border-gray-300"
                                onClick={() => handleViewPost(post)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Ver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-red-50 hover:border-red-300 border-gray-300"
                                onClick={() => post.id && handleDelete(post.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>

                  {/* Paginação */}
                  {totalPages > 1 && (
                    <div className="flex flex-col items-center gap-3 border-t pt-4">
                      <p className="text-sm text-gray-600">
                        Mostrando <span className="font-medium">{totalItems > 0 ? (currentPage - 1) * 10 + 1 : 0}</span> até{' '}
                        <span className="font-medium">{Math.min(currentPage * 10, totalItems)}</span> de{' '}
                        <span className="font-medium">{totalItems}</span> resultados
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePageChange(pageNum)}
                              className={
                                currentPage === pageNum
                                  ? 'bg-[#D4C070] hover:bg-[#849D70] text-white'
                                  : 'border-gray-300 text-[#68686A] hover:bg-[#EEF1F2]'
                              }
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="border-gray-300 text-[#68686A] hover:bg-[#EEF1F2] disabled:opacity-50"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categorias" className="m-0 pt-2">
          <BlogCategorias />
        </TabsContent>
      </Tabs>
    </main>
  );
}

export default BlogPage;
